import { BaseApiClient } from './base-client';
import type { ApiResponse, ImageGenerationParams, ImageGenerationResponse } from '../../types';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function sanitizeTeamId(teamId?: string | null): string | null {
  return teamId && UUID_REGEX.test(teamId) ? teamId : null;
}

/**
 * Image API - методы для генерации и управления изображениями
 */
export class ImageApi extends BaseApiClient {
  
  // ===== IMAGE GENERATION API =====
  
  /**
   * Генерация изображений
   */
  async generateImages(params: ImageGenerationParams): Promise<ApiResponse<ImageGenerationResponse>> {
    // Адаптируем под существующий backend формат
    // Для nano-banana и imagen: size - это aspectRatio (1:1, 16:9 и т.д.)
    // Для других: size - это разрешение (1024x1024 и т.д.)
    const payload = {
      prompt: params.prompt,
      provider: params.provider,
      model: params.model,
      // Team mode параметры
      ...(params.teamId && { teamId: params.teamId }),
      ...(params.folderId && { folderId: params.folderId }),
      // Bonus pool flags (Free Generations) — server validates and reserves a pool slot
      ...(params.useBonus && { useBonus: true }),
      ...(params.idempotencyKey && { idempotencyKey: params.idempotencyKey }),
      options: {
        // Для nano-banana/imagen параметр size (1:1, 16:9) идет в format (aspectRatio)
        // Для остальных size остается размером в пикселях
        format: params.format || params.size || '1:1',  // aspectRatio для Google моделей
        size: params.size || '1024x1024', // размер для остальных моделей
        quality: params.quality || 'medium',
        // gpt-image-2: отдельная ось разрешения (1K/2K/4K)
        ...(params.resolution && { resolution: params.resolution }),
        count: params.count || 1, // Количество изображений
        ...(params.compression && { compression: params.compression }),
        ...(params.background && { background: params.background }),
        // GPT Image параметры
        ...(params.inputFidelity && { inputFidelity: params.inputFidelity }),
        ...(params.style && { style: params.style }),
        // Информация о пресете
        ...(params.presetId && { presetId: params.presetId }),
        ...(params.presetName && { presetName: params.presetName }),
        // Recraft-specific parameters
        ...(params.vectorMode && { vectorMode: true }),
        // Nano Banana 2 fast mode
        ...(params.fastMode && { fastMode: true }),
        // Color Palette
        ...(params.colorPalette && params.colorPalette.length > 0 && { colorPalette: params.colorPalette }),
        // Cinematic Camera
        ...(params.cinematicCamera && { cinematicCamera: params.cinematicCamera }),
        // Studio Elements
        ...(params.elements && params.elements.length > 0 && { elements: params.elements }),
        // Mapped prompt for provider (with @mentions replaced by image references)
        ...(params.mappedPrompt && { mappedPrompt: params.mappedPrompt }),
        // Higgsfield-specific parameters
        ...(params.higgsfield && {
          higgsfield: {
            ...(params.higgsfield.style_id && { style_id: params.higgsfield.style_id }),
            ...(params.higgsfield.style_strength !== undefined && { style_strength: params.higgsfield.style_strength }),
            ...(params.higgsfield.custom_reference_id && { custom_reference_id: params.higgsfield.custom_reference_id }),
            ...(params.higgsfield.custom_reference_strength !== undefined && { custom_reference_strength: params.higgsfield.custom_reference_strength }),
            ...(params.higgsfield.enhance_prompt !== undefined && { enhance_prompt: params.higgsfield.enhance_prompt }),
            ...(params.higgsfield.seed && { seed: params.higgsfield.seed }),
          }
        }),
        // Midjourney-specific parameters
        ...(params.stylize !== undefined && { stylize: params.stylize }),
        ...(params.chaos !== undefined && { chaos: params.chaos }),
        ...(params.weird !== undefined && { weird: params.weird }),
        ...(params.seed !== undefined && { seed: params.seed }),
        ...(params.niji && { niji: params.niji }),
        ...(params.mjQuality !== undefined && { mjQuality: params.mjQuality })
      }
    };

    // Если есть файлы в S3, используем FormData
    if (params.s3Files && params.s3Files.length > 0) {
      const formData = new FormData();
      // Если промт пустой, используем дефолтный текст для image-to-image генерации
      const finalPrompt = params.prompt.trim() || 'Apply the selected style to this image';
      
      formData.append('prompt', finalPrompt);
      formData.append('provider', params.provider);
      formData.append('model', params.model);
      formData.append('options', JSON.stringify(payload.options));
      
      // Team mode параметры
      if (params.teamId) formData.append('teamId', params.teamId);
      if (params.folderId) formData.append('folderId', params.folderId);

      // Bonus pool flags — must be in FormData too, otherwise server falls back to token path
      if (params.useBonus) formData.append('useBonus', 'true');
      if (params.idempotencyKey) formData.append('idempotencyKey', params.idempotencyKey);

      // Файлы уже в S3 - отправляем ссылки
      const s3FilesJson = JSON.stringify(params.s3Files);
      formData.append('s3Files', s3FilesJson);

      const response = await this.client.post('/api/images/generate', formData, {
        // Content-Type удаляется в interceptor'е для FormData
        // Axios автоматически установит multipart/form-data с boundary
        // X-Telegram-Init-Data добавляется в interceptor'е
      });
      
      return response.data;
    } else {
      // Без файлов - обычный JSON
      const response = await this.client.post('/api/images/generate', payload);
      return response.data;
    }
  }
  
  /**
   * Server-side retry of a failed image generation via BullMQ queue.
   * Re-uses all original params (prompt, provider, references, etc.) stored in the DB.
   * Returns queued response — track via WebSocket/polling.
   */
  async regenerateImage(
    imageId: string,
    opts?: { useBonus?: boolean; idempotencyKey?: string }
  ): Promise<{
    success: boolean;
    queued: boolean;
    data: {
      jobId: string;
      generationId: string;
      status: string;
    };
  }> {
    const body: Record<string, any> = {};
    if (opts?.useBonus) body.useBonus = true;
    if (opts?.idempotencyKey) body.idempotencyKey = opts.idempotencyKey;
    const response = await this.client.post(`/api/images/generate/regenerate/${imageId}`, body);
    return response.data;
  }

  // ===== JOB STATUS (Queue polling) =====

  /**
   * Poll status of a queued image generation job
   */
  async getJobStatus(jobId: string): Promise<ApiResponse<{
    jobId: string;
    status: 'pending' | 'generating' | 'completed' | 'failed';
    generationId: string;
    result?: any;
    error?: string;
    createdAt?: number;
    finishedAt?: number;
  }>> {
    const response = await this.client.get(`/api/images/generate/job/${jobId}/status`);
    return response.data;
  }

  // ===== HIGGSFIELD API =====
  
  /**
   * Получить список доступных стилей Soul для Higgsfield
   */
  async getHiggsfieldStyles(): Promise<ApiResponse<import('../../types').HiggsfieldSoulStyle[]>> {
    const response = await this.client.get('/api/images/higgsfield/styles');
    return response.data;
  }
  
  /**
   * Проверить статус генерации Higgsfield по job_set_id
   */
  async getHiggsfieldJobStatus(jobSetId: string): Promise<ApiResponse<import('../../types').HiggsfieldJobSet>> {
    const response = await this.client.get(`/api/images/higgsfield/job-sets/${jobSetId}`);
    return response.data;
  }

  // ===== IMAGE HISTORY & MANAGEMENT =====

  /**
   * Загрузить историю сгенерированных изображений пользователя с пагинацией
   */
  async loadImageHistory(
    limit: number = 50, 
    teamId?: string, 
    folderId?: string, 
    memberFilter?: string,
    offset: number = 0
  ): Promise<ApiResponse<{ images: any[]; hasMore: boolean; offset: number; limit: number }>> {
    try {
      const params: Record<string, any> = { limit, offset };
      if (teamId) params.teamId = teamId;
      if (folderId) params.folderId = folderId;
      if (memberFilter) params.memberFilter = memberFilter;
      
      const response = await this.client.get('/api/images/history', { params });
      return response.data;
    } catch (error) {
      console.error('Error loading image history:', error);
      throw error;
    }
  }

  /**
   * Удалить изображение из базы данных и S3
   */
  async deleteImage(imageId: string): Promise<ApiResponse<{ imageId: string; s3Deleted: boolean; dbDeleted: boolean }>> {
    try {
      const response = await this.client.delete(`/api/images/${imageId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }

  /**
   * Пометить/снять пометку изображения как избранное
   */
  async toggleImageFavorite(imageId: string, isFavorite: boolean): Promise<ApiResponse<{ imageId: string; isFavorite: boolean }>> {
    try {
      const response = await this.client.post(`/api/images/${imageId}/favorite`, { isFavorite });
      return response.data;
    } catch (error) {
      console.error('Error toggling image favorite:', error);
      throw error;
    }
  }

  // ===== COLOR PALETTES API =====

  async getColorPalettes(teamId?: string | null): Promise<ApiResponse<{ palettes: Array<{ id: string; name: string; colors: { hex: string; ratio: number }[] }> }>> {
    const params = teamId ? { teamId } : {};
    const response = await this.client.get('/api/images/color-palettes', { params });
    return response.data;
  }

  async createColorPalette(name: string, colors: { hex: string; ratio: number }[], teamId?: string | null): Promise<ApiResponse<{ id: string; name: string; colors: { hex: string; ratio: number }[] }>> {
    const response = await this.client.post('/api/images/color-palettes', { name, colors, ...(teamId && { teamId }) });
    return response.data;
  }

  async deleteColorPalette(id: string): Promise<ApiResponse<void>> {
    const response = await this.client.delete(`/api/images/color-palettes/${id}`);
    return response.data;
  }

  /**
   * Апскейлить изображение с помощью Freepik API
   */
  async upscaleImage(params: {
    imageId: string;
    imageUrl: string;
    originalProvider: string;
    originalModel: string;
    scale_factor: string;
    optimized_for: string;
    target_resolution?: string;
    teamId?: string | null;
    resizedS3Key?: string;
    // Topaz-ветка (provider='topaz')
    topazFamily?: string;
    topazModel?: string;
    topazQuality?: string;
    topazParams?: Record<string, unknown>;
  }): Promise<{ success: boolean; queued: boolean; jobId: string; generationId: string; tokensDeducted: number }> {
    try {
      const response = await this.client.post('/api/images/upscale', {
        ...params,
        teamId: sanitizeTeamId(params.teamId),
      });
      return response.data;
    } catch (error) {
      console.error('Error upscaling image:', error);
      throw error;
    }
  }

  /**
   * Получить историю апскейлов пользователя
   */
  async getUpscaleHistory(limit: number = 50, offset: number = 0, teamId?: string | null): Promise<ApiResponse<{
    images: Array<{
      id: string;
      url: string;
      s3Key?: string;
      thumbnailUrl?: string;
      quality: string;
      createdAt: string;
      isUpscaled: boolean;
      provider: string;
      model: string;
    }>; 
    hasMore: boolean;
    offset: number;
    limit: number;
  }>> {
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (teamId) params.append('teamId', teamId);
      const response = await this.client.get(`/api/images/upscale/history?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error loading upscale history:', error);
      throw error;
    }
  }

  /**
   * Получить избранные изображения пользователя
   */
  async getFavoriteImages(limit: number = 50): Promise<ApiResponse<{ images: any[]; count: number }>> {
    try {
      const response = await this.client.get(`/api/images/favorites?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error loading favorite images:', error);
      throw error;
    }
  }

  // ===== FAVORITE PRESETS API =====

  /**
   * Получить избранные пресеты пользователя
   */
  async getFavoritePresets(params?: { provider?: string; presetType?: string }): Promise<ApiResponse<{ presets: any[]; count: number }>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.provider) queryParams.append('provider', params.provider);
      if (params?.presetType) queryParams.append('presetType', params.presetType);
      
      const response = await this.client.get(`/api/images/presets/favorites?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error loading favorite presets:', error);
      throw error;
    }
  }

  /**
   * Добавить пресет в избранное
   */
  async addFavoritePreset(params: {
    provider: string;
    model?: string;
    presetType: 'image_style' | 'higgsfield_soul' | 'video_motion' | 'custom';
    presetId: string;
    presetName?: string;
    presetData?: any;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.post('/api/images/presets/favorites', params);
      return response.data;
    } catch (error) {
      console.error('Error adding favorite preset:', error);
      throw error;
    }
  }

  /**
   * Удалить пресет из избранного
   */
  async removeFavoritePreset(params: {
    provider: string;
    presetType: string;
    presetId: string;
  }): Promise<ApiResponse<any>> {
    try {
      const queryParams = new URLSearchParams({
        provider: params.provider,
        presetType: params.presetType,
        presetId: params.presetId
      });
      
      const response = await this.client.delete(`/api/images/presets/favorites?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error removing favorite preset:', error);
      throw error;
    }
  }

  /**
   * Проверить, находится ли пресет в избранном
   */
  async isPresetFavorite(params: {
    provider: string;
    presetType: string;
    presetId: string;
  }): Promise<ApiResponse<{ isFavorite: boolean }>> {
    try {
      const queryParams = new URLSearchParams({
        provider: params.provider,
        presetType: params.presetType,
        presetId: params.presetId
      });
      
      const response = await this.client.get(`/api/images/presets/favorites/check?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error checking favorite preset:', error);
      throw error;
    }
  }

  // ===== ACTIVE GENERATIONS =====

  /**
   * Получить список активных генераций изображений
   * В team mode возвращает генерации всех участников команды
   * @param teamId - ID команды для team mode (опционально)
   * @param type - тип генерации: 'image' | 'upscale' | 'image_edit' | 'wardrobe' (по умолчанию 'image')
   */
  async getActiveImageGenerations(teamId?: string, type: string = 'image'): Promise<ApiResponse<{ generations: any[]; count: number; isTeamMode?: boolean }>> {
    try {
      const params: Record<string, string> = { type };
      if (teamId) params.teamId = teamId;
      
      const response = await this.client.get('/api/images/active-generations', { params });
      return response.data;
    } catch (error) {
      console.error('Error loading active image generations:', error);
      throw error;
    }
  }

  /**
   * Отменить активную генерацию изображения
   * @param generationId - ID генерации
   * @param type - тип генерации (по умолчанию 'image')
   */
  async cancelImageGeneration(generationId: string, type: string = 'image'): Promise<ApiResponse<{ generationId: string; cancelled: boolean }>> {
    try {
      const response = await this.client.delete(`/api/images/active-generations/${generationId}`, {
        params: { type }
      });
      return response.data;
    } catch (error) {
      console.error('Error cancelling image generation:', error);
      throw error;
    }
  }

  /**
   * Очистить все застрявшие активные генерации изображений
   * @param type - тип генерации (по умолчанию 'image')
   */
  async clearStuckImageGenerations(type: string = 'image'): Promise<ApiResponse<{ cleared: number; generationIds: string[] }>> {
    try {
      const response = await this.client.post('/api/images/active-generations/clear', { type });
      return response.data;
    } catch (error) {
      console.error('Error clearing stuck image generations:', error);
      throw error;
    }
  }

  // ===== STYLE PRESETS API (Admin-created presets) =====

  /**
   * Получить активные пресеты стилей для провайдера
   */
  async getStylePresets(provider: string): Promise<ApiResponse<{ presets: ImageStylePreset[] }>> {
    try {
      const response = await this.client.get(`/api/images/style-presets?provider=${provider}`);
      return response.data;
    } catch (error) {
      console.error('Error loading style presets:', error);
      throw error;
    }
  }

  /**
   * Получить промпт пресета по ID (для использования при генерации)
   */
  async getStylePresetPrompt(presetId: string): Promise<ApiResponse<{ id: string; name: string; prompt: string; requiresPrompt: boolean }>> {
    try {
      const response = await this.client.get(`/api/images/style-presets/${presetId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting style preset prompt:', error);
      throw error;
    }
  }

  // ===== ADMIN STYLE PRESETS API =====

  /**
   * [Admin] Получить все пресеты стилей
   */
  async adminGetAllStylePresets(provider?: string): Promise<ApiResponse<{ presets: ImageStylePresetAdmin[] }>> {
    try {
      const url = provider 
        ? `/api/admin/image-presets?provider=${provider}`
        : '/api/admin/image-presets';
      const response = await this.client.get(url);
      return response.data;
    } catch (error) {
      console.error('Error loading admin style presets:', error);
      throw error;
    }
  }

  /**
   * [Admin] Создать новый пресет стиля
   */
  async adminCreateStylePreset(data: FormData): Promise<ApiResponse<ImageStylePresetAdmin>> {
    try {
      const response = await this.client.post('/api/admin/image-presets', data);
      return response.data;
    } catch (error) {
      console.error('Error creating style preset:', error);
      throw error;
    }
  }

  /**
   * [Admin] Обновить пресет стиля
   */
  async adminUpdateStylePreset(presetId: string, data: FormData): Promise<ApiResponse<ImageStylePresetAdmin>> {
    try {
      const response = await this.client.put(`/api/admin/image-presets/${presetId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating style preset:', error);
      throw error;
    }
  }

  /**
   * [Admin] Удалить пресет стиля
   */
  async adminDeleteStylePreset(presetId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await this.client.delete(`/api/admin/image-presets/${presetId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting style preset:', error);
      throw error;
    }
  }

  // ===== IMAGE EDIT (INPAINT) API =====

  /**
   * Редактирование изображения с помощью AI (inpaint)
   */
  async editImage(params: {
    imageUrl: string;
    imageUrls?: string[]; // Multiple images for combined editing
    prompt: string;
    maskData?: string;
    aspectRatio?: '1:1' | '2:3' | '3:2' | '3:4' | '4:3' | '9:16' | '16:9' | '21:9';
    teamId?: string | null;
    /** For compact inpaint: crop rect in original image coordinates */
    cropRect?: { x: number; y: number; w: number; h: number };
    /** For compact inpaint: URL of the original full image (for server-side compositing) */
    originalImageUrl?: string;
    /** For compact inpaint: S3 key of the original — server snapshots it so UI-side deletion can't break compositing */
    originalS3Key?: string;
    // ── Iterations chain (desktop Image Edit) — all optional ─────────
    /** UUID edit_session (migration 141) — primary якорь цепочки. */
    sessionId?: string;
    /** S3-ключ корневого upload'а (якорь цепочки). Фронт передаёт его при каждой итерации. */
    rootUploadS3Key?: string;
    rootUploadS3Url?: string;
    /** URL непосредственного источника (parent iteration s3_url или root_upload_s3_url). */
    parentS3Url?: string;
    iterationType?: 'inpaint' | 'crop';
    /** Сохраняется в generation_options для persist маски между сессиями. */
    maskStrokes?: Array<{ points: Array<{ x: number; y: number }>; size: number }>;
    maskNaturalDims?: { w: number; h: number };
  }): Promise<{ success: boolean; queued: boolean; jobId: string; generationId: string; tokensDeducted: number; iterationSequence?: number; sessionId?: string }> {
    try {
      const response = await this.client.post('/api/images/edit', {
        ...params,
        teamId: sanitizeTeamId(params.teamId),
      });
      return response.data;
    } catch (error) {
      console.error('Error editing image:', error);
      throw error;
    }
  }

  /* ── Image Edit iterations chain (desktop-only UX) ──────────────────
   *  Линейная цепочка редактирований от одного оригинала. Endpoint prefix:
   *  /api/images/edit/iterations
   * ─────────────────────────────────────────────────────────────────── */

  /** Список уникальных оригиналов (для левой галереи Image Edit page).
   *  @deprecated использовать listEditSessions — migration 141 ввела sessions. */
  async getEditOriginals(teamId?: string | null): Promise<{
    success: boolean;
    originals: Array<{
      rootUploadS3Key: string;
      rootUploadS3Url: string;
      iterationCount: number;
      lastActivity: string;
    }>;
  }> {
    try {
      let url = '/api/images/edit/iterations/originals';
      if (teamId) url += `?teamId=${teamId}`;
      const response = await this.client.get(url);
      return response.data;
    } catch (error) {
      console.error('Error loading edit originals:', error);
      throw error;
    }
  }

  /** Список edit sessions пользователя (migration 141). */
  async listEditSessions(teamId?: string | null): Promise<{
    success: boolean;
    sessions: Array<EditSessionDTO>;
  }> {
    let url = '/api/images/edit/iterations/sessions';
    if (teamId) url += `?teamId=${teamId}`;
    const response = await this.client.get(url);
    return response.data;
  }

  /** Создать новую edit session. */
  async createEditSession(params: {
    rootS3Key: string;
    rootS3Url: string;
    name?: string;
    teamId?: string | null;
  }): Promise<{ success: boolean; session: EditSessionDTO }> {
    const response = await this.client.post('/api/images/edit/iterations/sessions', {
      ...params,
      teamId: sanitizeTeamId(params.teamId),
    });
    return response.data;
  }

  /** Получить iterations конкретной сессии. */
  async getEditSessionIterations(sessionId: string, teamId?: string | null): Promise<{
    success: boolean;
    session: EditSessionDTO;
    iterations: Array<EditIterationDTO>;
  }> {
    const params = new URLSearchParams();
    if (teamId) params.set('teamId', teamId);
    const qs = params.toString();
    const url = `/api/images/edit/iterations/sessions/${sessionId}/iterations${qs ? `?${qs}` : ''}`;
    const response = await this.client.get(url);
    return response.data;
  }

  /** Обновить session-level draft (prompt / refs композера). */
  async updateEditSessionDraft(
    sessionId: string,
    payload: {
      draftPrompt?: string;
      draftRefs?: Array<{ url: string; s3Key: string | null; thumbnailUrl: string | null }>;
    },
  ): Promise<{ success: boolean; session: EditSessionDTO }> {
    const response = await this.client.patch(
      `/api/images/edit/iterations/sessions/${sessionId}/draft`,
      payload,
    );
    return response.data;
  }

  /** Удалить сессию и все её iterations (cascade). */
  async deleteEditSession(sessionId: string): Promise<{ success: boolean; deletedId: string }> {
    const response = await this.client.delete(`/api/images/edit/iterations/sessions/${sessionId}`);
    return response.data;
  }

  /** Цепочка итераций по rootUploadS3Key. */
  async getEditIterationsByRoot(rootKey: string, teamId?: string | null): Promise<{
    success: boolean;
    iterations: Array<EditIterationDTO>;
  }> {
    try {
      const params = new URLSearchParams({ rootKey });
      if (teamId) params.set('teamId', teamId);
      const response = await this.client.get(`/api/images/edit/iterations/by-root?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error loading iterations chain:', error);
      throw error;
    }
  }

  /** Сохранить crop как итерацию (hide_from_gallery=true). Blob уже загружен в S3. */
  async saveCropIteration(params: {
    s3Key: string;
    s3Url: string;
    thumbnailUrl?: string;
    thumbnailS3Key?: string;
    /** UUID сессии (migration 141). Пустая строка / undefined → бэкенд auto-create. */
    sessionId?: string;
    rootUploadS3Key: string;
    rootUploadS3Url: string;
    parentS3Url: string;
    aspectRatio?: string;
    teamId?: string | null;
  }): Promise<{ success: boolean; iteration: EditIterationDTO; sessionId?: string }> {
    try {
      const response = await this.client.post('/api/images/edit/iterations/save-crop', {
        ...params,
        teamId: sanitizeTeamId(params.teamId),
      });
      return response.data;
    } catch (error) {
      console.error('Error saving crop iteration:', error);
      throw error;
    }
  }

  /** Retry failed iteration — UPDATE той же строки, новый job. */
  async retryIteration(iterationId: string): Promise<{
    success: boolean;
    queued: boolean;
    jobId: string;
    generationId: string;
    iterationId: string;
    tokensDeducted: number;
  }> {
    try {
      const response = await this.client.post(`/api/images/edit/iterations/retry/${iterationId}`);
      return response.data;
    } catch (error) {
      console.error('Error retrying iteration:', error);
      throw error;
    }
  }

  /** Сохранить или очистить маску на итерации (strokes + dims в generation_options). */
  async updateIterationMask(
    iterationId: string,
    payload: {
      maskStrokes: Array<{ points: Array<{ x: number; y: number }>; size: number }> | null;
      maskNaturalDims: { w: number; h: number } | null;
    },
  ): Promise<{ success: boolean; iteration: EditIterationDTO }> {
    try {
      const response = await this.client.patch(`/api/images/edit/iterations/${iterationId}/mask`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating iteration mask:', error);
      throw error;
    }
  }

  /** Удалить одну итерацию (+ S3 cleanup на бэке). */
  async deleteIteration(iterationId: string): Promise<{ success: boolean; deletedId: string }> {
    try {
      const response = await this.client.delete(`/api/images/edit/iterations/${iterationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting iteration:', error);
      throw error;
    }
  }

  /** Удалить всю цепочку итераций оригинала (+ S3 cleanup). */
  async deleteIterationChain(rootUploadS3Key: string, teamId?: string | null): Promise<{ success: boolean; deletedCount: number }> {
    try {
      const encoded = encodeURIComponent(rootUploadS3Key);
      let url = `/api/images/edit/iterations/chain/${encoded}`;
      if (teamId) url += `?teamId=${teamId}`;
      const response = await this.client.delete(url);
      return response.data;
    } catch (error) {
      console.error('Error deleting iteration chain:', error);
      throw error;
    }
  }

  /**
   * Получить историю редактирований пользователя
   */
  async getImageEditHistory(limit: number = 50, offset: number = 0, teamId?: string | null): Promise<ApiResponse<{
    images: Array<{
      id: string;
      url: string;
      s3Key?: string;
      thumbnailUrl?: string;
      prompt: string;
      createdAt: string;
      provider: string;
      model: string;
      /** Permanent copy оригинала (до edit) — for compare slider before/after */
      originalUrl?: string | null;
      originalS3Key?: string | null;
      // Iteration chain — присутствует у строк что в цепочке итераций.
      sessionId?: string | null;
      rootUploadS3Key?: string | null;
      rootUploadS3Url?: string | null;
      iterationType?: 'crop' | 'inpaint' | null;
      iterationSequence?: number | null;
    }>;
    hasMore: boolean;
    offset: number;
    limit: number;
  }>> {
    try {
      let url = `/api/images/edit/history?limit=${limit}&offset=${offset}`;
      if (teamId) url += `&teamId=${teamId}`;
      const response = await this.client.get(url);
      return response.data;
    } catch (error) {
      console.error('Error loading edit history:', error);
      throw error;
    }
  }
}

/** Shape edit session (migration 141). */
export interface EditSessionDTO {
  id: string;
  userId: string;
  teamId: string | null;
  rootS3Key: string;
  rootS3Url: string;
  name: string | null;
  draftPrompt: string | null;
  draftRefs: Array<{ url: string; s3Key: string | null; thumbnailUrl: string | null }>;
  createdAt: string;
  lastActivityAt: string;
  iterationCount?: number;
}

/** Shape итерации возвращаемой бэкендом (mapIterationRow в iterations.routes.js). */
export interface EditIterationDTO {
  id: string;
  url: string | null;
  s3Key: string | null;
  thumbnailUrl: string | null;
  prompt: string;
  createdAt: string;
  aspectRatio: string;
  /** Session identity (migration 141) — primary якорь цепочки. */
  sessionId: string | null;
  rootUploadS3Key: string | null;
  rootUploadS3Url: string | null;
  parentS3Url: string | null;
  iterationType: 'crop' | 'inpaint' | null;
  iterationSequence: number | null;
  hideFromGallery: boolean;
  status: 'completed' | 'failed' | 'pending' | string;
  errorMessage: string | null;
  originalUrl: string | null;
  maskStrokes: Array<{ points: Array<{ x: number; y: number }>; size: number }> | null;
  maskNaturalDims: { w: number; h: number } | null;
  /** References приложенные при генерации этой итерации (composer attachments). */
  referenceImages?: Array<{ url: string; s3Key: string | null; thumbnailUrl: string | null }>;
}

// Types for style presets
export interface ImageStylePreset {
  id: string;
  name: string;
  previewUrl: string | null;
  requiresPrompt: boolean;
  usageCount: number;
  composerHint?: string | null; // Подсказка для пользователя в композере
}

export interface ImageStylePresetAdmin extends ImageStylePreset {
  prompt: string;
  previewS3Key: string | null;
  provider: string;
  isActive: boolean;
  sortOrder: number;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
}

